import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import { useBoundaries } from "../../api/admin";
import {
  useBounds,
  useNowPlaying,
  useTimeline,
  useWaveform,
  WAVEFORM_MAX_SPAN_MS,
} from "../../api/queries";
import type { TimelineWindow } from "../../api/types";
import { type FetchWindow, fetchWindow, sameWindow, waveformWindow } from "../geometry";
import {
  markDirty as markCanvasDirty,
  setBounds,
  setNowPlaying,
  setSnapTargets,
  setWaveform,
  setWindowData,
  type TimelineRefs,
} from "./state";

/** What the component and the extracted hooks need back out of the query layer. */
export interface TimelineFeed {
  /** The loaded window, as the query cache holds it. */
  data: TimelineWindow | undefined;
  /** Re-derive both request windows from the view as it stands now. */
  syncWindow: () => void;
  /** The newest recorded instant the loaded window knows about, if any. */
  latestMs: () => number | null;
  /** Tell the animation loop its last frame is stale. */
  markDirty: () => void;
}

/**
 * Re-derive both request windows from the view as it stands now.
 *
 * A plain function for the same reason the writers in ./state are: it reads the view through
 * `r`, and a hook that closes over that cannot be memoised by React's compiler.
 */
function syncTo(
  r: TimelineRefs,
  setWindow: Dispatch<SetStateAction<FetchWindow>>,
  setWaveWindow: Dispatch<SetStateAction<FetchWindow | null>>,
): void {
  const next = fetchWindow(r.view.current);
  setWindow((current) => (sameWindow(current, next) ? current : next));
  // Its own window: the envelope cannot afford the timeline's two viewports of margin.
  const wave = waveformWindow(r.view.current, WAVEFORM_MAX_SPAN_MS);
  setWaveWindow((current) => (sameWindow(current, wave) ? current : wave));
}

function useWindows(r: TimelineRefs) {
  const [window_, setWindow] = useState(() => fetchWindow(r.view.current));
  const [waveWindow, setWaveWindow] = useState(() =>
    waveformWindow(r.view.current, WAVEFORM_MAX_SPAN_MS),
  );
  const pending = useRef<number | null>(null);
  const firstSync = useRef(true);
  const syncWindow = useCallback(() => {
    if (firstSync.current) {
      firstSync.current = false;
      syncTo(r, setWindow, setWaveWindow);
      return;
    }
    // Follow updates must not keep postponing the latest viewport's request.
    if (pending.current !== null) return;
    pending.current = window.setTimeout(() => {
      pending.current = null;
      syncTo(r, setWindow, setWaveWindow);
    }, 250);
  }, [r]);
  useEffect(
    () => () => {
      window.clearTimeout(pending.current ?? undefined);
      pending.current = null;
    },
    [],
  );
  return { window_, waveWindow, syncWindow };
}

/** Everything the animation loop reads outside React, copied into refs as it arrives. */
function useMirror(r: TimelineRefs, q: ReturnType<typeof useQueries>): void {
  const { data, waveform, waveWindow, nowPlaying, boundaryData, bounds, language } = q;
  useEffect(() => {
    // Cleared outright once the viewport outruns what the route will answer: the last response
    // is still cached, and drawing it would strand a band across part of the screen.
    setWaveform(r, waveWindow ? (waveform ?? null) : null);
  }, [r, waveform, waveWindow]);
  useEffect(() => setNowPlaying(r, nowPlaying ?? null), [r, nowPlaying]);
  useEffect(() => {
    const parse = (values: string[] | undefined) => (values ?? []).map((v) => Date.parse(v));
    setSnapTargets(r, parse(boundaryData?.icy), parse(boundaryData?.scheduled));
  }, [r, boundaryData]);
  // Held in a ref as well as in the query cache, because the animation loop reads it
  // outside React and must not re-subscribe to get the current value. The previous window
  // stays on screen until the next one arrives rather than blanking the canvas.
  useEffect(() => setWindowData(r, data), [r, data]);
  useEffect(() => setBounds(r, bounds ?? null), [r, bounds]);
  // The ruler and the block labels are painted, not rendered, so React re-rendering on a
  // language change is not enough -- the loop has to be told the frame is stale.
  useEffect(() => markCanvasDirty(r), [r, language]);
}

function useQueries(
  editable: boolean,
  window_: ReturnType<typeof fetchWindow>,
  waveWindow: ReturnType<typeof waveformWindow>,
) {
  const { data } = useTimeline(
    window_.startMs,
    window_.endMs,
    window_.width,
    // Polled only while the window reaches into the present, which is the only case where the
    // server has anything new to say about it.
    window_.endMs > Date.now(),
  );
  const { data: waveform } = useWaveform(waveWindow, window_.endMs > Date.now());
  // The title log, which knows what is playing a couple of minutes before the segments table
  // does. Already polled and already fed by the push stream, so this costs nothing extra.
  const { data: nowPlaying } = useNowPlaying();
  // Snap targets, fetched only in the admin view: a public visitor never drags an edge and
  // should not pay for the query. Also held back until the container has been measured,
  // because before that the window is a single instant and the range is empty.
  const boundariesReady = editable && window_.width > 0;
  const { data: boundaryData } = useBoundaries(
    boundariesReady ? new Date(window_.startMs).toISOString() : null,
    boundariesReady ? new Date(window_.endMs).toISOString() : null,
  );
  // How far the data reaches either side, which is a different question from what the loaded
  // window happens to contain. Mirrored out of React for the same reason the delay is: both
  // wall callbacks read it outside a render.
  const { data: bounds } = useBounds();
  const { i18n } = useTranslation();
  return { data, waveform, waveWindow, nowPlaying, boundaryData, bounds, language: i18n.language };
}

export function useTimelineFeed(r: TimelineRefs, editable: boolean): TimelineFeed {
  const { window_, waveWindow, syncWindow } = useWindows(r);
  const q = useQueries(editable, window_, waveWindow);
  useMirror(r, q);
  const markDirty = useCallback(() => markCanvasDirty(r), [r]);
  /** The newest recorded instant the loaded window knows about, if any. */
  const latestMs = useCallback(() => {
    const value = r.data.current?.latest;
    return value ? Date.parse(value) : null;
  }, [r]);
  return useMemo(
    () => ({ data: q.data, syncWindow, latestMs, markDirty }),
    [q.data, syncWindow, latestMs, markDirty],
  );
}
